import { FORM_BUILDER_FIELD_TYPE } from "@loanbossinc/component-library";

import {
  shouldShowDscrDebtServiceTermTrailingMonthsEdit,
  shouldShowDscrDebtServiceTermProjectedMonthsEdit,
  shouldHypotheticalAmortizationsEdit
} from "./section-financial-covenants-rows-logic";

const frequencyOptions = [
  { name: "One Time", id: "oneTime" },
  { name: "Monthly", id: "monthly" },
  { name: "Quarterly", id: "quarterly" },
  { name: "Semi Annually", id: "semiAnnually" },
  { name: "Annually", id: "annually" }
];

export const rows = {
  title: {
    isTitle: true,
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.TEXT,
        value: "Financial Covenants",
        style: {
          marginLeft: "0",
          fontFamily: "Work Sans",
          fontStyle: "normal",
          fontWeight: "500",
          fontSize: "16px",
          lineHeight: "14px",
          textTransform: "uppercase",
          color: "#000000",
          marginBottom: "10px"
        }
      },
      {
        type: FORM_BUILDER_FIELD_TYPE.REFERENCE,
        referencedFieldPath: "loans.financialcovenants.header"
      }
    ]
  },
  dscrStabilized: {
    label: "DSCR (Stabilized)",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.NUMBER,
        name: "covenants.dscrStabilized",
        variant: "filled",
        decimals: 2
      },
      {
        type: FORM_BUILDER_FIELD_TYPE.REFERENCE,
        referencedFieldPath: "loans.covenants.dscrStabilized"
      }
    ]
  },
  dscrTestingBeginsFrequency: {
    label: "DSCR Testing Frequency",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.TYPEAHEAD,
        name: "covenants.dscrFrequency",
        options: frequencyOptions,
        displayWith: option => option.name,
        setValueWith: option => option.id,
        variant: "filled"
      },
      {
        type: FORM_BUILDER_FIELD_TYPE.DATE,
        name: "covenants.dscrBeginTestingDate",
        variant: "filled",
        hideLabel: false,
        label: "DSCR Testing Begins",
        style: { marginTop: "16px" }
      }
    ]
  },
  dscrCovenants: {
    label: "DSCR Covenants",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.STRING,
        name: "covenants.dscrCovenants",
        variant: "filled",
        multiline: true
      },
      {
        type: FORM_BUILDER_FIELD_TYPE.REFERENCE,
        referencedFieldPath: "loans.covenants.dscrCovenants"
      }
    ]
  },
  dscrType: {
    label: "DSCR Type",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.TYPEAHEAD,
        name: "covenants.dscrTypeId",
        variant: "filled",
        parentId: "dscrTypes",
        displayWith: option => option.name,
        setValueWith: option => option.id
      }
    ]
  },
  trailingMonths: {
    isVisible: covenants => shouldShowDscrDebtServiceTermTrailingMonthsEdit(covenants),
    label: "Trailing Months",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.NUMBER,
        name: "covenants.dscrDebtServiceTerm",
        variant: "filled"
      }
    ]
  },
  projectedMonths: {
    isVisible: covenants => shouldShowDscrDebtServiceTermProjectedMonthsEdit(covenants),
    label: "Projected Months",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.NUMBER,
        name: "covenants.dscrDebtServiceTerm",
        variant: "filled"
      }
    ]
  },
  dscrAmortizationTerm: {
    isVisible: covenants => shouldHypotheticalAmortizationsEdit(covenants),
    label: "DSCR Amortization Term",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.NUMBER,
        name: "covenants.dscrAmortTerm",
        variant: "filled"
      }
    ]
  },
  dscrFixedRate: {
    isVisible: covenants => shouldHypotheticalAmortizationsEdit(covenants),
    label: "DSCR Fixed Rate",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.PERCENT,
        name: "covenants.dscrFixedRate",
        decimals: 3,
        variant: "filled"
      },
      {
        type: FORM_BUILDER_FIELD_TYPE.REFERENCE,
        referencedFieldPath: "loans.covenants.dscrFixedRate"
      }
    ]
  },

  dscrTreasurySpread: {
    isVisible: covenants => shouldHypotheticalAmortizationsEdit(covenants),
    label: "DSCR Treasury Spread",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.PERCENT,
        name: "covenants.dscrTreasurySpread",
        decimals: 3,
        variant: "filled"
      },
      {
        type: FORM_BUILDER_FIELD_TYPE.REFERENCE,
        referencedFieldPath: "loans.covenants.dscrTreasurySpread"
      }
    ]
  },
  dscrTreasuryLookback: {
    isVisible: covenants => shouldHypotheticalAmortizationsEdit(covenants),
    label: "DSCR Treasury Lookback",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.PERCENT,
        name: "covenants.dscrTreasuryLookback",
        variant: "filled",
        onKeyDown: event => event.key === "-" && event.preventDefault()
      }
    ]
  },
  dscrFloatingRateSpread: {
    isVisible: covenants => shouldHypotheticalAmortizationsEdit(covenants),
    label: "DSCR Floating Rate Spread",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.PERCENT,
        name: "covenants.dscrFloatingRateSpread",
        decimals: 3,
        variant: "filled"
      },
      {
        type: FORM_BUILDER_FIELD_TYPE.REFERENCE,
        referencedFieldPath: "loans.covenants.dscrFloatingRateSpread"
      }
    ]
  },
  debtYieldStabilized: {
    label: "Debt Yield (Stabilized)",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.PERCENT,
        name: "covenants.debtYieldStabilized",
        variant: "filled",
        multiline: true,
        decimals: 3
      },
      {
        type: FORM_BUILDER_FIELD_TYPE.REFERENCE,
        referencedFieldPath: "loans.covenants.debtYieldStabilized"
      }
    ]
  },
  debtYieldTestingBeginsFrequency: {
    label: "Debt Yield Testing Frequency",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.TYPEAHEAD,
        name: "covenants.debtYieldFrequency",
        options: frequencyOptions,
        displayWith: option => option.name,
        setValueWith: option => option.id,
        variant: "filled"
      },
      {
        type: FORM_BUILDER_FIELD_TYPE.DATE,
        name: "covenants.debtYieldBeginTestingDate",
        variant: "filled",
        hideLabel: false,
        label: "Debt Yield Testing Begins",
        style: { marginTop: "16px" }
      }
    ]
  },
  debtYieldCovenants: {
    label: "Debt Yield Covenants",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.STRING,
        name: "covenants.debtYieldCovenants",
        variant: "filled",
        multiline: true
      },
      {
        type: FORM_BUILDER_FIELD_TYPE.REFERENCE,
        referencedFieldPath: "loans.covenants.debtYieldCovenants"
      }
    ]
  },
  ltvStabilized: {
    label: "LTV (Stabilized)",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.PERCENT,
        name: "covenants.ltvStabilized",
        variant: "filled",
        decimals: 3
      },
      {
        type: FORM_BUILDER_FIELD_TYPE.REFERENCE,
        referencedFieldPath: "loans.covenants.ltvStabilized"
      }
    ]
  },
  ltvTestingBeginsFrequency: {
    label: "LTV Testing Frequency",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.TYPEAHEAD,
        name: "covenants.ltvFrequency",
        options: frequencyOptions,
        displayWith: option => option.name,
        setValueWith: option => option.id,
        variant: "filled"
      },
      {
        type: FORM_BUILDER_FIELD_TYPE.DATE,
        name: "covenants.ltvBeginTestingDate",
        variant: "filled",
        hideLabel: false,
        label: "LTV Testing Begins",
        style: { marginTop: "16px" }
      }
    ]
  },
  ltvCovenants: {
    label: "LTV Covenants",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.STRING,
        name: "covenants.ltvCovenants",
        variant: "filled",
        multiline: true
      },
      {
        type: FORM_BUILDER_FIELD_TYPE.REFERENCE,
        referencedFieldPath: "loans.covenants.ltvCovenants"
      }
    ]
  },
  occupancy: {
    label: "Occupancy",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.PERCENT,
        name: "covenants.occupancy",
        variant: "filled",
        decimals: 3
      },
      {
        type: FORM_BUILDER_FIELD_TYPE.REFERENCE,
        referencedFieldPath: "loans.covenants.occupancy"
      }
    ]
  },
  occupancyTestingBeginsFrequency: {
    label: "Occupancy Testing Frequency",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.TYPEAHEAD,
        name: "covenants.occupancyFrequency",
        options: frequencyOptions,
        displayWith: option => option.name,
        setValueWith: option => option.id,
        variant: "filled"
      },
      {
        type: FORM_BUILDER_FIELD_TYPE.DATE,
        name: "covenants.occupancyBeginTestingDate",
        variant: "filled",
        hideLabel: false,
        label: "Occupancy Testing Begins",
        style: { marginTop: "16px" }
      }
    ]
  },
  occupancyCovenants: {
    label: "Occupancy Covenants",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.STRING,
        name: "covenants.occupancyCovenants",
        variant: "filled",
        multiline: true
      },
      {
        type: FORM_BUILDER_FIELD_TYPE.REFERENCE,
        referencedFieldPath: "loans.covenants.occupancyCovenants"
      }
    ]
  },
  ltc: {
    label: "LTC",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.PERCENT,
        name: "covenants.ltc",
        variant: "filled",
        decimals: 3
      },
      {
        type: FORM_BUILDER_FIELD_TYPE.REFERENCE,
        referencedFieldPath: "loans.covenants.ltc"
      }
    ]
  },
  ltcTestingBeginsFrequency: {
    label: "LTC Testing Frequency",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.TYPEAHEAD,
        name: "covenants.ltcFrequency",
        options: frequencyOptions,
        displayWith: option => option.name,
        setValueWith: option => option.id,
        variant: "filled"
      },
      {
        type: FORM_BUILDER_FIELD_TYPE.DATE,
        name: "covenants.ltcBeginTestingDate",
        variant: "filled",
        hideLabel: false,
        label: "LTC Testing  Begins",
        style: { marginTop: "16px" }
      }
    ]
  },
  ltcCovenants: {
    label: "LTC Covenants",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.STRING,
        name: "covenants.ltcCovenants",
        variant: "filled",
        multiline: true
      },
      {
        type: FORM_BUILDER_FIELD_TYPE.REFERENCE,
        referencedFieldPath: "loans.covenants.ltcCovenants"
      }
    ]
  },
  dscrAsIs: {
    label: "DSCR (As-Is)",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.NUMBER,
        name: "covenants.dscrAsIs",
        variant: "filled",
        decimals: 2
      }
    ]
  },
  debtYieldAsIs: {
    label: "Debt Yield (As-Is)",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.PERCENT,
        name: "covenants.debtYieldAsIs",
        variant: "filled",
        decimals: 3
      }
    ]
  },
  ltvAsIs: {
    label: "LTV (As-Is)",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.PERCENT,
        name: "covenants.ltvAsIs",
        variant: "filled",
        decimals: 3
      }
    ]
  },
  misc: {
    label: "Misc",
    cells: [
      {
        type: FORM_BUILDER_FIELD_TYPE.STRING,
        name: "covenants.financialCovenantsMisc",
        variant: "filled",
        multiline: true
      },
      {
        type: FORM_BUILDER_FIELD_TYPE.REFERENCE,
        referencedFieldPath: "loans.covenants.financialCovenantsMisc"
      }
    ]
  }
};
